import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { onboardingCheckStarted, onboardingReset, onboardingStateLoaded } from '../../stores/OnboardingStore'
import { loadOnboardingState } from '../../services/OnboardingService'
import {
  inviteLinkErrorMessage,
  joinCompanyByInviteLink,
  takePendingCompanyInvite,
} from '../../services/CompanyInviteLinkService'

/**
 * Uses a workspace invite link saved before sign-up or sign-in, so the state that follows already
 * reflects the new membership. Returns why it failed, or null.
 */
async function joinSavedInviteLink(): Promise<string | null> {
  const token = takePendingCompanyInvite()
  if (!token) return null
  try {
    await joinCompanyByInviteLink(token)
    return null
  } catch (err) {
    return `Your invite link couldn’t be used: ${inviteLinkErrorMessage(err)}`
  }
}

/** What the signed-in shell shows: nothing yet, the wizard, or the app as usual. */
export type OnboardingGate = 'checking' | 'wizard' | 'app'

/**
 * Asks the cloud once per signed-in account whether its onboarding is active (SC-438). While
 * `enabled` is false (signed out, Terms screen still due, guests) nothing is checked and the app
 * shows as usual.
 */
export function useOnboardingGate(enabled: boolean, userId: string | null): OnboardingGate {
  const dispatch = useAppDispatch()
  const onboarding = useAppSelector((s) => s.onboarding)
  const checkedFor = onboarding.phase === 'idle' ? null : onboarding.userId

  useEffect(() => {
    if (!enabled || !userId) {
      if (!userId && onboarding.phase !== 'idle') dispatch(onboardingReset())
      return
    }
    if (checkedFor === userId) return
    dispatch(onboardingCheckStarted(userId))
    void joinSavedInviteLink().then(async (notice) => {
      const state = await loadOnboardingState()
      dispatch(onboardingStateLoaded({ userId, state, notice }))
    })
  }, [enabled, userId, checkedFor, onboarding.phase, dispatch])

  if (!enabled || !userId) return 'app'
  if (checkedFor !== userId || onboarding.phase === 'checking') return 'checking'
  return onboarding.state.active ? 'wizard' : 'app'
}
